/** Low-level pixel-art drawing primitives shared by the map, close-ups and monitors. */

export type Ctx = CanvasRenderingContext2D;

export const INK = "#1b1426";

/** Cycle through a palette list. */
export const cyc = <T>(arr: readonly T[], i: number): T => arr[((i % arr.length) + arr.length) % arr.length] as T;

export function R(c: Ctx, col: string, x: number, y: number, w: number, h: number): void {
  c.fillStyle = col;
  c.fillRect(x, y, w, h);
}

/** Filled box with a 1px outline. */
export function obox(c: Ctx, fill: string, x: number, y: number, w: number, h: number, line?: string): void {
  R(c, line ?? INK, x - 1, y - 1, w + 2, h + 2);
  R(c, fill, x, y, w, h);
}

/** 3×5 bitmap font for monitor digits and labels. */
const PF: Record<string, string[]> = {
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  "2": ["111", "001", "111", "100", "111"],
  "3": ["111", "001", "111", "001", "111"],
  "4": ["101", "101", "111", "001", "001"],
  "5": ["111", "100", "111", "001", "111"],
  "6": ["111", "100", "111", "101", "111"],
  "7": ["111", "001", "010", "010", "010"],
  "8": ["111", "101", "111", "101", "111"],
  "9": ["111", "101", "111", "001", "111"],
  "/": ["001", "001", "010", "100", "100"],
  "%": ["101", "001", "010", "100", "101"],
  ".": ["0", "0", "0", "0", "1"],
  ":": ["0", "1", "0", "1", "0"],
  "-": ["000", "000", "111", "000", "000"],
  "+": ["000", "010", "111", "010", "000"],
  x: ["000", "101", "010", "101", "000"],
  " ": ["00", "00", "00", "00", "00"],
  A: ["010", "101", "111", "101", "101"],
  B: ["110", "101", "110", "101", "110"],
  C: ["011", "100", "100", "100", "011"],
  D: ["110", "101", "101", "101", "110"],
  E: ["111", "100", "110", "100", "111"],
  F: ["111", "100", "110", "100", "100"],
  G: ["011", "100", "101", "101", "011"],
  H: ["101", "101", "111", "101", "101"],
  I: ["111", "010", "010", "010", "111"],
  J: ["001", "001", "001", "101", "010"],
  K: ["101", "101", "110", "101", "101"],
  L: ["100", "100", "100", "100", "111"],
  M: ["10001", "11011", "10101", "10001", "10001"],
  N: ["1001", "1101", "1011", "1001", "1001"],
  O: ["010", "101", "101", "101", "010"],
  P: ["110", "101", "110", "100", "100"],
  Q: ["010", "101", "101", "110", "011"],
  R: ["110", "101", "110", "101", "101"],
  S: ["011", "100", "010", "001", "110"],
  T: ["111", "010", "010", "010", "010"],
  U: ["101", "101", "101", "101", "111"],
  V: ["101", "101", "101", "101", "010"],
  W: ["10001", "10001", "10101", "11011", "10001"],
  Y: ["101", "101", "010", "010", "010"],
  X: ["101", "101", "010", "101", "101"],
  Z: ["111", "001", "010", "100", "111"],
};

/** Draw text in the bitmap font; returns the x after the last glyph. */
export function ptext(c: Ctx, str: string | number, x: number, y: number, col: string, s = 1): number {
  c.fillStyle = col;
  let cx = Math.round(x);
  for (const ch of String(str)) {
    const g = PF[ch] ?? PF[ch.toUpperCase()];
    if (!g) {
      cx += 3 * s;
      continue;
    }
    g.forEach((row, r) => {
      for (let k = 0; k < row.length; k++) if (row[k] === "1") c.fillRect(cx + k * s, Math.round(y) + r * s, s, s);
    });
    cx += ((g[0]?.length ?? 3) + 1) * s;
  }
  return cx;
}

/** Filled pixel ellipse. */
export function pell(c: Ctx, cx: number, cy: number, rx: number, ry: number, col: string): void {
  c.fillStyle = col;
  for (let y = -ry; y <= ry; y++) {
    const w = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
    c.fillRect(Math.round(cx - w), Math.round(cy + y), w * 2 + 1, 1);
  }
}

/** Bresenham line with square brush of width t. */
export function pline(c: Ctx, x0: number, y0: number, x1: number, y1: number, col: string, t = 1): void {
  c.fillStyle = col;
  x0 = Math.round(x0);
  y0 = Math.round(y0);
  x1 = Math.round(x1);
  y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let e = dx + dy;
  for (let i = 0; i < 600; i++) {
    c.fillRect(x0 - (t >> 1), y0 - (t >> 1), t, t);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * e;
    if (e2 >= dy) {
      e += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      e += dx;
      y0 += sy;
    }
  }
}

/** 4×4 ordered-dither threshold matrix (0..15). */
export const BAYER: readonly number[] = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/** Ordered-dither a rectangle at the given density (0..1). */
export function dither(c: Ctx, x: number, y: number, w: number, h: number, col: string, level: number): void {
  if (level <= 0) return;
  c.fillStyle = col;
  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++)
      if ((BAYER[((y + j) & 3) * 4 + ((x + i) & 3)] ?? 0) < level * 16) c.fillRect(x + i, y + j, 1, 1);
}
